import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, Grid, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { ChartLegend, seriesColors, TimeChart } from "./opportunityCharts";
import { barometerComplete, barometerRank, DASH, fmtCount, fmtDate, fmtNumber, fmtPct, MIN_RANK_POINTS, splitBarometer } from "./opportunityFormat";

const BAND_TEXT = {
    high: 'Viele Treffer: oberes Viertel der eigenen Geschichte',
    mid: 'Mittlerer Bereich der eigenen Geschichte',
    low: 'Wenige Treffer: unteres Viertel der eigenen Geschichte',
};

function Stat({ label, value, note }) {
    return (
        <Box>
            <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>{label}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
            {note && <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>{note}</Typography>}
        </Box>
    );
}

function pointTooltip(point) {
    const row = point.data;
    return (
        <>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{fmtCount(row.y)} Treffer</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
                {fmtCount(row.healthy)} gesund von {fmtCount(row.universe)}, Median {fmtPct(row.medianYield)}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                {row.isLive ? 'Wochenlauf' : 'Rückblick, Quartalsende'}
            </Typography>
        </>
    );
}

function toChartPoint(row, isLive) {
    return {
        x: row.as_of,
        y: row.hit_count,
        healthy: row.healthy_count,
        universe: row.universe_count,
        medianYield: row.median_yield,
        isLive,
    };
}

const Barometer = ({ current, points, error }) => {
    const theme = useTheme();
    const palette = seriesColors(theme.palette.mode);
    const split = splitBarometer(points);
    const history = split.history.filter(barometerComplete);
    const live = split.live.filter(barometerComplete);
    const complete = barometerComplete(current);
    const rank = complete ? barometerRank(current, history) : null;
    const hasChart = history.length + live.length > 1;

    const series = [
        { id: 'Rückblick', color: palette.primary, data: history.map((row) => toChartPoint(row, false)), pointRadius: 3 },
        { id: 'Wochenläufe', color: palette.secondary, data: live.map((row) => toChartPoint(row, true)), pointRadius: 5, showLine: live.length > 1 },
    ].filter((serie) => serie.data.length > 0);

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1.5 }}>Marktbarometer</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}><Stat label="Treffer aktuell" value={fmtCount(current?.hit_count)} /></Grid>
                            <Grid item xs={6}><Stat label="Universum" value={fmtCount(current?.universe_count)} /></Grid>
                            <Grid item xs={6}><Stat label="Gesunde Papiere" value={fmtCount(current?.healthy_count)} /></Grid>
                            <Grid item xs={6}><Stat label="Median-Rendite" value={fmtPct(current?.median_yield)} note="der gesunden Papiere" /></Grid>
                        </Grid>
                        <Box sx={{ mt: 2 }}>
                            {rank ? (
                                <>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{BAND_TEXT[rank.band]}</Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        Von {fmtCount(rank.n)} Quartalsenden seit {fmtDate(rank.firstDay)} hatten {fmtCount(rank.below)} weniger,
                                        {' '}{fmtCount(rank.equal)} gleich viele und {fmtCount(rank.above)} mehr Treffer.
                                        Median {fmtNumber(rank.median, rank.median % 1 ? 1 : 0)}, Spanne {fmtCount(rank.min)} bis {fmtCount(rank.max)}.
                                    </Typography>
                                    {rank.share !== null && (
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            Das Universum war früher kleiner. Anteil an den gesunden Papieren: {fmtPct(rank.share)},
                                            {' '}Median im Rückblick {fmtPct(rank.shareMedian)}, höher als an {fmtCount(rank.shareBelow)} von {fmtCount(rank.shareCount)} Stichtagen.
                                        </Typography>
                                    )}
                                </>
                            ) : !complete && current ? (
                                <>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Daten noch unvollständig</Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        Nur {fmtCount(current.healthy_count)} von {fmtCount(current.universe_count)} Papieren gelten als gesund, meist fehlen noch Geschäftszahlen.
                                        Die Trefferzahl sagt deshalb noch nichts über den Markt.
                                    </Typography>
                                </>
                            ) : (
                                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                                    Für eine Einordnung fehlen Vergleichswerte: Es braucht mindestens {MIN_RANK_POINTS} Stichtage im Rückblick.
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Trefferzahl im Zeitverlauf</Typography>
                        {error && <Typography variant="body2" color="error">Verlauf konnte nicht geladen werden: {error}</Typography>}
                        {!error && !hasChart && (
                            <Typography variant="body2" sx={{ opacity: 0.75, mt: 1 }}>
                                {split.history.length + split.live.length > 0
                                    ? 'Der Verlauf erscheint, sobald die Daten vollständig sind.'
                                    : 'Noch kein Verlauf vorhanden.'}
                            </Typography>
                        )}
                        {!error && hasChart && (
                            <>
                                <ChartLegend items={[
                                    ...(history.length ? [{ kind: 'line', color: palette.primary, label: 'Rückblick: ein Punkt je Quartalsende' }] : []),
                                    ...(live.length ? [{ kind: 'dot', color: palette.secondary, label: 'Wochenläufe: ein Punkt je Lauf' }] : []),
                                    ...(rank ? [{ kind: 'dash', color: theme.palette.text.secondary, label: 'Median im Rückblick' }] : []),
                                ]} />
                                <TimeChart
                                    series={series}
                                    yMin={0}
                                    yFormat={(value) => fmtCount(value)}
                                    markers={rank ? [{ value: rank.median, label: '' }] : []}
                                    renderTooltip={pointTooltip}
                                    ariaLabel={`Trefferzahl im Zeitverlauf, aktuell ${fmtCount(current?.hit_count)}`}
                                />
                                <Accordion disableGutters elevation={0} sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                        <Typography variant="caption">Werte als Tabelle</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 0 }}>
                                        <TableContainer sx={{ maxHeight: 280 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Stichtag</TableCell>
                                                        <TableCell>Art</TableCell>
                                                        <TableCell align="right">Treffer</TableCell>
                                                        <TableCell align="right">Gesund</TableCell>
                                                        <TableCell align="right">Universum</TableCell>
                                                        <TableCell align="right">Median</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {[...live.map((row) => ({ row, kind: 'Wochenlauf' })), ...[...history].reverse().map((row) => ({ row, kind: 'Quartalsende' }))].map(({ row, kind }) => (
                                                        <TableRow key={`${kind}-${row.as_of}`}>
                                                            <TableCell>{fmtDate(row.as_of)}</TableCell>
                                                            <TableCell>{kind}</TableCell>
                                                            <TableCell align="right">{fmtCount(row.hit_count)}</TableCell>
                                                            <TableCell align="right">{fmtCount(row.healthy_count)}</TableCell>
                                                            <TableCell align="right">{fmtCount(row.universe_count)}</TableCell>
                                                            <TableCell align="right">{row.median_yield == null ? DASH : fmtPct(row.median_yield)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        )}
                    </Grid>
                </Grid>
                <Stack sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>
                        Das Barometer beschreibt die Zahl der Papiere auf der Liste, nicht deren spätere Kursentwicklung.
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default Barometer;
